/**
 * Card terminal checkouts at the till (#3500, staff-api #3499).
 *
 * The server owns the state: a card payment becomes successful only after
 * SumUp confirms it, and the checkout is recorded server-side once cash and
 * cards cover the amount. The till polls the checkout while it is open.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export type TerminalCheckoutStatus = 'open' | 'completed' | 'partially_recorded' | 'abandoned' | 'record_failed';
export type TerminalPaymentStatus = 'pending' | 'successful' | 'failed' | 'cancelled' | 'unknown';

export interface TerminalPayment {
  id: string;
  source: 'terminal' | 'manual';
  readerId: string | null;
  amountCents: number;
  status: TerminalPaymentStatus;
  paymentReference: string | null;
  cardLast4: string | null;
  cardType: string | null;
  failureReason: string | null;
  recorded: boolean;
  createdAt: string;
  confirmedAt: string | null;
}

export interface TerminalCheckout {
  id: string;
  invoiceId: string;
  status: TerminalCheckoutStatus;
  amountCents: number;
  cashAmountCents: number;
  cashReceivedCents: number;
  remainingCardCents: number;
  receiptEmail: string | null;
  paymentBatchId: string | null;
  receipt: { id: string; receiptNumber: string; recipientEmail: string | null } | null;
  payments: TerminalPayment[];
  createdAt: string;
  closedAt: string | null;
}

interface CheckoutResponse {
  checkout: TerminalCheckout;
}

/** How often the till asks for the result while a checkout is open. */
export const TERMINAL_POLL_INTERVAL_MS = 2000;

const terminalKey = (checkoutId: string) => ['terminal-checkouts', checkoutId] as const;

export function isTerminalCheckoutClosed(checkout: TerminalCheckout): boolean {
  return checkout.status !== 'open';
}

export function useTerminalCheckout(checkoutId: string | null) {
  return useQuery({
    queryKey: terminalKey(checkoutId ?? 'none'),
    queryFn: async () => (await api.get<CheckoutResponse>(`/terminal-checkouts/${encodeURIComponent(checkoutId ?? '')}`)).checkout,
    enabled: checkoutId !== null,
    refetchInterval: (query) => (query.state.data && isTerminalCheckoutClosed(query.state.data) ? false : TERMINAL_POLL_INTERVAL_MS),
    retry: false,
  });
}

const openCheckoutKey = (invoiceId: string) => ['terminal-checkouts', 'open', invoiceId] as const;

/** The invoice's open checkout, so a till that closed the dialog can resume it. */
export function useOpenTerminalCheckoutForInvoice(invoiceId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: openCheckoutKey(invoiceId ?? 'none'),
    queryFn: async () =>
      (await api.get<{ checkout: TerminalCheckout | null }>(`/invoices/${encodeURIComponent(invoiceId ?? '')}/terminal-checkouts/open`)).checkout,
    enabled: enabled && invoiceId !== undefined,
    staleTime: 0,
    retry: false,
  });
}

/**
 * Everything that changes when a checkout ends, whoever ended it (this till,
 * a webhook or the sweep). Called once, from where the till observes the
 * end: the open-checkout cache must not offer the finished one for resuming.
 */
export function refreshAfterTerminalCheckoutClosed(queryClient: QueryClient, invoiceId: string): void {
  queryClient.setQueryData(openCheckoutKey(invoiceId), null);
  void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
}

/** Writes return the checkout; the poll cache is replaced so the panel sees the change at once. */
function useTerminalMutation<TInput>(request: (input: TInput) => Promise<CheckoutResponse>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TInput) => (await request(input)).checkout,
    onSuccess: (checkout) => {
      queryClient.setQueryData(terminalKey(checkout.id), checkout);
    },
  });
}

export interface OpenTerminalCheckoutInput {
  invoiceId: string;
  cashAmountCents: number;
  cashReceivedCents: number;
  receiptEmail?: string;
  idempotencyKey: string;
}

export function useOpenTerminalCheckout() {
  return useTerminalMutation(({ invoiceId, idempotencyKey, ...body }: OpenTerminalCheckoutInput) =>
    api.post<CheckoutResponse>(`/invoices/${encodeURIComponent(invoiceId)}/terminal-checkouts`, body, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  );
}

export function useStartTerminalCardPayment() {
  return useTerminalMutation(({ checkoutId, ...body }: { checkoutId: string; readerId: string; amountCents: number }) =>
    api.post<CheckoutResponse>(`/terminal-checkouts/${encodeURIComponent(checkoutId)}/card-payments`, body),
  );
}

export function useManualTerminalCardPayment() {
  return useTerminalMutation(
    ({ checkoutId, ...body }: { checkoutId: string; amountCents: number; paymentReference: string }) =>
      api.post<CheckoutResponse>(`/terminal-checkouts/${encodeURIComponent(checkoutId)}/manual-card-payments`, body),
  );
}

export function useCloseTerminalCheckout() {
  return useTerminalMutation((checkoutId: string) =>
    api.post<CheckoutResponse>(`/terminal-checkouts/${encodeURIComponent(checkoutId)}/close`, {}),
  );
}

export function useCancelTerminalPayment() {
  return useTerminalMutation((paymentId: string) =>
    api.post<CheckoutResponse>(`/terminal-payments/${encodeURIComponent(paymentId)}/cancel`, {}),
  );
}

export function useResolveTerminalPaymentFailed() {
  return useTerminalMutation((paymentId: string) =>
    api.post<CheckoutResponse>(`/terminal-payments/${encodeURIComponent(paymentId)}/resolve-failed`, {}),
  );
}

const READER_STORAGE_KEY = 'staff-portal-terminal-reader';

/**
 * The terminal to preselect: `preferred` if still paired, else the one
 * remembered at this till, else the first.
 */
export function defaultReaderId(readers: readonly { id: string }[], preferred?: string | null): string {
  const paired = (id: string | null | undefined): id is string => id != null && readers.some((r) => r.id === id);
  if (paired(preferred)) return preferred;
  const remembered = rememberedReaderId();
  if (paired(remembered)) return remembered;
  return readers[0]?.id ?? '';
}

/** The terminal chosen at this browser's till, remembered per browser. */
export function rememberedReaderId(): string | null {
  try {
    return window.localStorage.getItem(READER_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function rememberReaderId(readerId: string): void {
  try {
    window.localStorage.setItem(READER_STORAGE_KEY, readerId);
  } catch {
    // Private mode or blocked storage: the till simply asks again.
  }
}
